// src/modules/ambassadors/components/AmbassadorStats.tsx - Карточки статистики + подарочный набор

import React from 'react';
import { Row, Col, Card, Statistic, Spin, Button, Space, Tag } from 'antd';
import {
  UserOutlined,
  DollarOutlined,
  GiftOutlined,
  TeamOutlined,
  EditOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { AmbassadorProgramStats, AmbassadorSettings } from '@/types/models/ambassador';

interface AmbassadorStatsProps {
  stats: AmbassadorProgramStats | null;
  settings: AmbassadorSettings | null;
  loading?: boolean;
  onEditGift?: () => void;
}

/**
 * Определяет цвет для индикатора остатков
 */
const getStockColor = (stock: number): string => {
  if (stock > 5) return 'green';
  if (stock >= 1 && stock <= 5) return 'orange';
  return 'red';
};

/**
 * Определяет иконку для индикатора остатков
 */
const getStockIcon = (stock: number): React.ReactNode => {
  if (stock > 5) return '✅';
  if (stock >= 1 && stock <= 5) return <WarningOutlined style={{ color: '#faad14' }} />;
  return '❌';
};

export const AmbassadorStats: React.FC<AmbassadorStatsProps> = ({
  stats,
  settings,
  loading = false,
  onEditGift,
}) => {
  if (loading) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  const giftItem = settings?.welcome_gift_item_details;
  const stockAvailable = giftItem?.stock_available || 0;

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      {/* Статистика амбассадоров */}
      <Col xs={24} sm={12} md={6} lg={5}>
        <Card variant="borderless">
          <Statistic
            title="Всего амбассадоров"
            value={stats?.total_ambassadors || 0}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            Активных: {stats?.active_ambassadors || 0} | Неактивных: {stats?.inactive_ambassadors || 0}
          </div>
        </Card>
      </Col>

      {/* Привлечено клиентов */}
      <Col xs={24} sm={12} md={6} lg={5}>
        <Card variant="borderless">
          <Statistic
            title="Привлечено клиентов"
            value={stats?.total_linked_clients || 0}
            prefix={<TeamOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            Через промокоды амбассадоров
          </div>
        </Card>
      </Col>

      {/* Продажи */}
      <Col xs={24} sm={12} md={6} lg={5}>
        <Card variant="borderless">
          <Statistic
            title="Продажи"
            value={stats?.total_lifetime_sales || 0}
            prefix={<DollarOutlined />}
            suffix="₸"
            valueStyle={{ color: '#faad14' }}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            Через амбассадоров
          </div>
        </Card>
      </Col>

      {/* Выплачено бонусов */}
      <Col xs={24} sm={12} md={6} lg={5}>
        <Card variant="borderless">
          <Statistic
            title="Выплачено бонусов"
            value={stats?.total_bonuses_paid || 0}
            prefix={<GiftOutlined />}
            suffix="₸"
            valueStyle={{ color: '#f5222d' }}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            Всего начислено
          </div>
        </Card>
      </Col>

      {/* ✅ НОВАЯ КАРТОЧКА: Подарочный набор */}
      <Col xs={24} sm={24} md={12} lg={4}>
        <Card
          variant="borderless"
          title={
            <Space size="small">
              <GiftOutlined style={{ color: '#faad14', fontSize: 14 }} />
              <span style={{ fontSize: 13 }}>Подарочный набор</span>
            </Space>
          }
          extra={
            onEditGift && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={onEditGift}
                style={{ padding: 0 }}
              />
            )
          }
          styles={{ header: { minHeight: 38, padding: '8px 16px' }, body: { padding: '12px 16px' } }}
        >
          {!giftItem ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                Не выбран
              </div>
              {onEditGift && (
                <Button type="primary" size="small" onClick={onEditGift}>
                  Выбрать
                </Button>
              )}
            </div>
          ) : (
            <div>
              {/* Первый ряд: Название и Цена */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 8,
                gap: 8,
              }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}>
                  {giftItem.name}
                </div>
                <div style={{ 
                  fontSize: 12, 
                  color: '#8c8c8c',
                  whiteSpace: 'nowrap',
                }}>
                  {giftItem.price.toLocaleString('ru-RU')} ₸
                </div>
              </div>

              {/* Второй ряд: Остатки и Предупреждение */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}>
                <Tag
                  color={getStockColor(stockAvailable)}
                  style={{ margin: 0, fontSize: 11 }}
                >
                  {getStockIcon(stockAvailable)} {stockAvailable} шт.
                </Tag>

                {/* Предупреждение при низких остатках */}
                {stockAvailable >= 1 && stockAvailable <= 5 && (
                  <div style={{
                    padding: '2px 6px',
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: 4,
                    fontSize: 10,
                    color: '#ad6800',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}>
                    ⚠️ Заканчивается!
                  </div>
                )}

                {/* Критическое предупреждение при 0 остатке */}
                {stockAvailable === 0 && (
                  <div style={{
                    padding: '2px 6px',
                    background: '#fff1f0',
                    border: '1px solid #ffa39e',
                    borderRadius: 4,
                    fontSize: 10,
                    color: '#cf1322',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}>
                    🚨 Закончился!
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
};